import React from 'react';
import PolicyQuestions from '../components/preChats/PolicyQuestions';
import ClaimReview from '../components/preChats/ClaimReview';
import ReviseRejectedClaim from '../components/preChats/ReviseRejectedClaim';
import AppealLetters from '../components/preChats/AppealLetters';
import AppealAClaim from '../components/preChats/AppealAClaim';
import { EDI_FILE_ACCEPT_EXTENSIONS } from '../services/fileParser';
import type { IChannel } from '../types/chat';

/** File picker `accept`: txt, png, json, bmp, jpeg, html, tiff, xml, doc, docx, csv, md, pdf, xlsx, xls, webp, jpg, EDI */
export const DEFAULT_FILE_ACCEPT = [
    '.txt',
    'text/plain',
    '.png',
    'image/png',
    '.json',
    'application/json',
    '.bmp',
    'image/bmp',
    '.jpeg',
    'image/jpeg',
    '.html',
    'text/html',
    '.tif',
    '.tiff',
    'image/tiff',
    '.xml',
    'application/xml',
    'text/xml',
    '.doc',
    'application/msword',
    '.docx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.csv',
    'text/csv',
    '.md',
    'text/markdown',
    '.pdf',
    'application/pdf',
    '.xlsx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xls',
    'application/vnd.ms-excel',
    '.webp',
    'image/webp',
    '.jpg',
    'image/jpeg',
    ...EDI_FILE_ACCEPT_EXTENSIONS,
].join(',');


export type ChannelConfig = {
    title: string;
    subtitle?: string;
    systemPrompt?: string;
    Component?: React.ComponentType<any>; // single renderable per channel (intro and/or form)
    attach?: {
        accept?: string; // input accept attribute
        multiple?: boolean;
    };
    placeholder?: string;
    tagColor?:string;
    agent_name?: string; // Name of the agent for this channel
};

export const channelsConfig: Record<string, ChannelConfig> = {
    'policy-questions': {
        title: 'Policy Questions',
        subtitle: 'Ask about health insurer policies and coverage details',
        systemPrompt: 'You are an assistant specialized in payer policy clarification and guidance.',
        Component: ({ onSubmit }) => (
            <PolicyQuestions onSubmit={(onSubmit as any)} />
        ),
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Ask a policy question…',
        tagColor: '#E0CFF0', // pastel blue
        agent_name: 'policy_researcher',
    },
    'claim-review': {
        title: 'Claim Review',
        subtitle: 'Upload claims for validation and improvement suggestions',
        systemPrompt: 'You review claim data and highlight errors or missing information.',
        Component: ClaimReview,
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Describe the claim issue…',
        tagColor: '#CBF2B7', // pastel orange
        agent_name: 'general_question',
    },
    'revise-rejected-claim': {
        title: 'Revise Rejected Claim',
        subtitle: 'Fix and resubmit claims that were rejected by payers',
        systemPrompt: 'You help revise rejected claims by identifying denial reasons and proposing corrections.',
        Component: () => <ReviseRejectedClaim />,
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Paste denial details or attach the claim JSON…',
        tagColor: '#CBF2B7', // pastel purple
        agent_name: 'general_question',
    },
    'appeal-letters': {
        title: 'Appeal Letters',
        subtitle: 'Generate, edit, and download professional appeal letters',
        systemPrompt: 'You draft concise, persuasive appeal letters grounded in payer policy and clinical justification.',
        Component: () => <AppealLetters />,
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Describe the denial and what you want to appeal…',
        tagColor: '#B8EDFF', // pastel green
        agent_name: 'general_question',
    },
    'appeal-claim': {
        title: 'Appeal a Claim',
        subtitle: 'Challenge denied claims with AI-powered appeal strategies',
        systemPrompt: 'You guide the user through appealing a specific denied claim with evidence and citations.',
        Component: () => <AppealAClaim />,
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Provide claim ID, payer, and denial reason…',
        tagColor: '#CBF2B7', // pastel pink
        agent_name: 'general_question',
    },
    'contract-research': {
        title: 'Contract Research',
        subtitle: 'Research payer contracts and reimbursement terms',
        systemPrompt: 'You help extract payer contract terms, coverage limitations, and reimbursement rates.',
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Ask about contract terms, coverage, or reimbursement…',
        tagColor: '#F2DAB7', // pastel yellow
        agent_name: 'policy_researcher',
    },
    'pre-service-review': {
        title: 'Pre-Service Review',
        subtitle: 'Upload your surgical scheduling documents by attaching the Scheduling Request Form, Clinical Records, and Prior Authorization Letter (if available) using the paperclip icon in the chatbox below or drag & drop them here.',
        systemPrompt: 'You review the surgical scheduling documents and provide a checklist of any issues found.',
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Pre-Service Review',
        tagColor: '#CBF2B7', // pastel pink
        agent_name: 'pre_service_reviewer',
    },
    'new': {
        title: '',
        subtitle: '',
        systemPrompt: 'Ask me anything!',
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Ask about contract terms, coverage, or reimbursement…',
        tagColor: '#CFCFC4', // pastel gray
        agent_name: 'general_question',
    },
    'unknown': {
        title: '',
        subtitle: '',
        systemPrompt: 'Ask me anything!',
        attach: { accept: DEFAULT_FILE_ACCEPT, multiple: true },
        placeholder: 'Ask about contract terms, coverage, or reimbursement…',
        tagColor: '#E0BBE4', // pastel lavender
        agent_name: 'general_question',
    },
};

/** Resolves a chat's channel config the same way ChatThumbnail tags its row: by
 * chat.tags first, then by chat.name (when it isn't a bare UUID), falling back
 * to 'unknown'. */
export function getChannelConfigForChat(
    chat: Pick<IChannel, 'tags' | 'name'> | null | undefined,
): ChannelConfig {
    if (chat?.tags && Array.isArray(chat.tags) && chat.tags.length > 0) {
        for (const tag of chat.tags) {
            if (channelsConfig[tag]) {
                return channelsConfig[tag];
            }
        }
    }

    if (chat?.name && !chat.name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        if (channelsConfig[chat.name]) {
            return channelsConfig[chat.name];
        }
    }

    return channelsConfig['unknown'];
}

